import { Calendar, ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { activeFilterKeys, clearFilters, readFilters, writeFilters, type FilterDef, type FilterValues, type PeriodPreset, type PeriodValue } from '@despachador/domain';
import { focusables, useLayer } from './layer';

/**
 * One way to filter a list (UX-022), choose a period (UX-023) and explain an icon or a figure
 * (UX-024). Redesign DS-010: FilterBar, PeriodPicker and Tooltip as drawn in Claude Design.
 */

// ---------------------------------------------------------------- media
/** Below this width popovers become bottom sheets and the quick filter a button (README §Responsive). */
const SHEET_QUERY = '(max-width: 859px)';
const subscribeMedia = (cb: () => void) => {
  const m = window.matchMedia(SHEET_QUERY);
  m.addEventListener('change', cb);
  return () => m.removeEventListener('change', cb);
};
export const useSheetLayout = () =>
  useSyncExternalStore(
    subscribeMedia,
    () => window.matchMedia(SHEET_QUERY).matches,
    () => false,
  );

// ---------------------------------------------------------------- popover
/**
 * Panel anchored to its trigger on desktop; a bottom sheet with its own title on phones. Focus
 * moves in and comes back to the trigger; Escape or a click outside close it.
 */
export function Popover({
  anchor,
  title,
  onClose,
  children,
  footer,
  width = 320,
}: {
  anchor: RefObject<HTMLElement | null>;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const sheet = useSheetLayout();
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayer(ref, onClose, { trap: sheet, initialFocus: () => (ref.current ? (focusables(ref.current)[0] ?? ref.current) : null) });

  useLayoutEffect(() => {
    if (sheet) return;
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos({ top: r.bottom + 6, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, sheet, width]);

  useEffect(() => {
    if (sheet) return;
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.current?.contains(t)) onClose();
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [anchor, onClose, sheet]);

  const panel = (
    <div
      ref={ref}
      className={sheet ? 'popover sheet' : 'popover'}
      role="dialog"
      aria-modal={sheet || undefined}
      aria-labelledby={titleId}
      tabIndex={-1}
      style={sheet ? undefined : { top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
    >
      <div className="popover-head">
        <h2 id={titleId}>{title}</h2>
        {sheet && (
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Cerrar">
            <X size={18} aria-hidden />
          </button>
        )}
      </div>
      <div className="popover-body">{children}</div>
      {footer && <div className="popover-foot">{footer}</div>}
    </div>
  );
  return createPortal(
    sheet ? (
      <div className="overlay sheet-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        {panel}
      </div>
    ) : (
      panel
    ),
    document.body,
  );
}

// ---------------------------------------------------------------- tooltip
type Side = 'top' | 'bottom' | 'left' | 'right';
const GAP = 8;
const EDGE = 8;

/** Where the bubble fits: the requested side, else the opposite one; always inside the viewport. */
function placeTip(anchor: DOMRect, tip: DOMRect, side: Side): { top: number; left: number; side: Side } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fits: Record<Side, boolean> = {
    top: anchor.top - tip.height - GAP >= EDGE,
    bottom: anchor.bottom + tip.height + GAP <= vh - EDGE,
    left: anchor.left - tip.width - GAP >= EDGE,
    right: anchor.right + tip.width + GAP <= vw - EDGE,
  };
  const opposite: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const s = fits[side] ? side : fits[opposite[side]] ? opposite[side] : side;
  const clampX = (x: number) => Math.max(EDGE, Math.min(x, vw - tip.width - EDGE));
  const clampY = (y: number) => Math.max(EDGE, Math.min(y, vh - tip.height - EDGE));
  if (s === 'top' || s === 'bottom') {
    return { side: s, left: clampX(anchor.left + anchor.width / 2 - tip.width / 2), top: s === 'top' ? anchor.top - tip.height - GAP : anchor.bottom + GAP };
  }
  return { side: s, top: clampY(anchor.top + anchor.height / 2 - tip.height / 2), left: s === 'left' ? anchor.left - tip.width - GAP : anchor.right + GAP };
}

/**
 * The single tooltip: inverse surface, opens on hover, keyboard focus and a tap (another tap or
 * Escape closes it), switches side near an edge. Text in plain words: never codes, SQL or ids.
 * The child must be one focusable element (a button or a link).
 */
export function Tooltip({ content, side = 'top', children }: { content: ReactNode; side?: Side; children: ReactElement<any> }) {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; side: Side } | null>(null);
  const touched = useRef(false);

  useLayoutEffect(() => {
    if (!open) return setPos(null);
    const a = anchorRef.current?.getBoundingClientRect();
    const t = tipRef.current?.getBoundingClientRect();
    if (a && t) setPos(placeTip(a, t, side));
  }, [open, side, content]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const away = (e: Event) => !anchorRef.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('touchstart', away);
    window.addEventListener('scroll', away, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('touchstart', away);
      window.removeEventListener('scroll', away, true);
    };
  }, [open]);

  if (!isValidElement(children)) return children;
  const child = children as ReactElement<any>;
  const p = child.props;
  const trigger = cloneElement(child, {
    ref: (el: HTMLElement | null) => {
      anchorRef.current = el;
    },
    'aria-describedby': open ? id : p['aria-describedby'],
    onMouseEnter: (e: any) => {
      p.onMouseEnter?.(e);
      if (!touched.current) setOpen(true);
    },
    onMouseLeave: (e: any) => {
      p.onMouseLeave?.(e);
      setOpen(false);
    },
    onFocus: (e: any) => {
      p.onFocus?.(e);
      if (!touched.current) setOpen(true);
    },
    onBlur: (e: any) => {
      p.onBlur?.(e);
      setOpen(false);
    },
    onTouchStart: (e: any) => {
      p.onTouchStart?.(e);
      touched.current = true;
      setOpen((o) => !o);
    },
  });
  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div ref={tipRef} id={id} role="tooltip" className={`tooltip ${pos?.side ?? side}`} style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}>
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------- period
export const PERIOD_LABEL: Record<PeriodPreset, string> = {
  today: 'Hoy',
  yesterday: 'Ayer',
  last7: 'Últimos 7 días',
  last30: 'Últimos 30 días',
  this_month: 'Este mes',
  last_month: 'Mes anterior',
  this_year: 'Este año',
  custom: 'Personalizado',
};
const LIST_PRESETS: PeriodPreset[] = ['today', 'yesterday', 'last7', 'last30', 'this_month', 'custom'];

const shortDay = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' }).format(new Date(y!, m! - 1, day!)).replace('.', '');
};
export const periodText = (v: PeriodValue) => (v.preset === 'custom' && v.from && v.to ? (v.from === v.to ? shortDay(v.from) : `${shortDay(v.from)} – ${shortDay(v.to)}`) : PERIOD_LABEL[v.preset]);

/** Presets plus a custom range; the choice is applied when the person picks it (or «Aplicar» for a range). */
export function PeriodPicker({
  value,
  onChange,
  presets = LIST_PRESETS,
  label = 'Período',
  today,
}: {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
  presets?: PeriodPreset[];
  label?: string;
  /** Local date of the tenant (YYYY-MM-DD): the last day a range may end. */
  today: string;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PeriodValue>(value);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => setDraft(value), [value, open]);
  const rangeError = draft.preset === 'custom' && draft.from && draft.to && draft.from > draft.to ? 'La fecha inicial debe ser anterior o igual a la final.' : null;
  const pick = (preset: PeriodPreset) => {
    if (preset === 'custom') return setDraft({ preset, from: draft.from ?? today, to: draft.to ?? today });
    onChange({ preset });
    close();
  };
  return (
    <>
      <button ref={anchor} type="button" className="btn filter-trigger period-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Calendar size={16} aria-hidden />
        <span className="sr-only">{label}: </span>
        <span className="filter-trigger-value">{periodText(value)}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <Popover
          anchor={anchor}
          title={label}
          onClose={close}
          footer={
            draft.preset === 'custom' ? (
              <button
                type="button"
                className="btn primary"
                disabled={!draft.from || !draft.to || !!rangeError}
                onClick={() => {
                  onChange(draft);
                  close();
                }}
              >
                Aplicar
              </button>
            ) : undefined
          }
        >
          <div className="choice-list" role="radiogroup" aria-label={label}>
            {presets.map((p) => (
              <button key={p} type="button" role="radio" aria-checked={draft.preset === p} className={draft.preset === p ? 'active' : ''} onClick={() => pick(p)}>
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>
          {draft.preset === 'custom' && (
            <div className="period-range">
              <label className="field">
                <span className="field-label">Desde</span>
                <input className="input" type="date" max={draft.to || today} value={draft.from ?? ''} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Hasta</span>
                <input className="input" type="date" min={draft.from} max={today} value={draft.to ?? ''} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
              </label>
              {rangeError && (
                <p className="field-error" role="alert">
                  {rangeError}
                </p>
              )}
            </div>
          )}
        </Popover>
      )}
    </>
  );
}

// ---------------------------------------------------------------- url state
/**
 * The filters of a list read from and written to the URL. Pass the router's search params
 * (`useSearchParams()` in react-router) so links and «atrás» restore the same view.
 */
export function useUrlFilters(defs: FilterDef[], [params, setParams]: [URLSearchParams, (next: URLSearchParams, opts?: { replace?: boolean }) => void]) {
  const values = useMemo(() => readFilters(params, defs), [params, defs]);
  const active = useMemo(() => activeFilterKeys(values, defs), [values, defs]);
  const set = useCallback((patch: FilterValues) => setParams(new URLSearchParams(writeFilters(params, defs, patch)), { replace: true }), [params, defs, setParams]);
  const clear = useCallback((keys?: string[]) => setParams(new URLSearchParams(clearFilters(params, defs, keys)), { replace: true }), [params, defs, setParams]);
  return { values, active, set, clear };
}

// ---------------------------------------------------------------- filter bar
export interface QuickOption<T extends string = string> {
  value: T;
  label: string;
  /** Shown only when the API gives it (counts per status are not available everywhere). */
  count?: number;
}

export interface FilterChipItem {
  key: string;
  /** «Conductor: Jorge Rincón». */
  label: string;
  onRemove: () => void;
}

/** Quick filter: a segmented row on wide screens, a button that opens a sheet on phones. */
function QuickFilter<T extends string>({ options, value, onChange, label }: { options: QuickOption<T>[]; value: T; onChange: (v: T) => void; label: string }) {
  const sheet = useSheetLayout();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const current = options.find((o) => o.value === value) ?? options[0];
  const text = (o: QuickOption<T>) => (
    <>
      {o.label}
      {o.count !== undefined && <span className="count">{o.count.toLocaleString('es-CO')}</span>}
    </>
  );
  if (!sheet)
    return (
      <div className="segmented quick-filter" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={o.value === value ? 'active' : ''} onClick={() => onChange(o.value)}>
            {text(o)}
          </button>
        ))}
      </div>
    );
  return (
    <>
      <button ref={anchor} type="button" className="btn filter-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <span className="sr-only">{label}: </span>
        {/* the first option is «all»: name the filter instead of «Todos» */}
        <span className="filter-trigger-value">{current && current !== options[0] ? current.label : label}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <Popover anchor={anchor} title={label} onClose={close}>
          <div className="choice-list" role="radiogroup" aria-label={label}>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={o.value === value}
                className={o.value === value ? 'active' : ''}
                onClick={() => {
                  onChange(o.value);
                  close();
                }}
              >
                {text(o)}
              </button>
            ))}
          </div>
        </Popover>
      )}
    </>
  );
}

/**
 * The bar above every list: search · quick filter · period · «Más filtros», then the chips of the
 * active filters with «Limpiar todo» and the number of results. Every part is optional.
 */
export function FilterBar<T extends string>({
  search,
  quick,
  period,
  more,
  chips = [],
  onClearAll,
  results,
  label = 'Filtros',
}: {
  search?: ReactNode;
  quick?: { options: QuickOption<T>[]; value: T; onChange: (v: T) => void; label: string };
  period?: ReactNode;
  /** Secondary filters: their form goes in a popover (sheet on phones) with «Aplicar» and «Limpiar». */
  more?: { content: ReactNode; onApply: () => void; onClear: () => void; activeCount: number };
  chips?: FilterChipItem[];
  onClearAll?: () => void;
  /** «128 servicios»: shown when filters are active, announced politely. */
  results?: string | null;
  label?: string;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <section className="filter-bar" role="search" aria-label={label}>
      <div className="filter-row">
        {search && <div className="filter-search">{search}</div>}
        {quick && <QuickFilter {...quick} />}
        {period}
        {more && (
          <button ref={anchor} type="button" className="btn filter-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
            <SlidersHorizontal size={16} aria-hidden />
            Filtros
            {more.activeCount > 0 && <span className="count">{more.activeCount}</span>}
          </button>
        )}
      </div>
      {open && more && (
        <Popover
          anchor={anchor}
          title="Más filtros"
          onClose={close}
          width={360}
          footer={
            <>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  more.onClear();
                  close();
                }}
              >
                Limpiar
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  more.onApply();
                  close();
                }}
              >
                Aplicar
              </button>
            </>
          }
        >
          <div className="stack">{more.content}</div>
        </Popover>
      )}
      {(chips.length > 0 || results) && (
        <div className="filter-chips">
          {chips.map((c) => (
            <span key={c.key} className="filter-chip">
              {c.label}
              <button type="button" aria-label={`Quitar ${c.label}`} onClick={c.onRemove}>
                <X size={14} aria-hidden />
              </button>
            </span>
          ))}
          {chips.length > 0 && onClearAll && (
            <button type="button" className="btn ghost sm" onClick={onClearAll}>
              Limpiar todo
            </button>
          )}
          {results && (
            <span className="filter-results" aria-live="polite">
              {results}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- quick filter over a loaded list
export type ListFilters<R> = Record<string, { label: string; match: (row: R) => boolean }>;

/**
 * A quick filter applied to the rows already loaded (lists top at 200): the counts always add up
 * and the choice lives in the URL (`?estado=`). The first entry is «all» and is never written.
 */
export function useListFilter<R>(
  rows: R[] | null | undefined,
  filters: ListFilters<R>,
  [params, setParams]: [URLSearchParams, (next: URLSearchParams, opts?: { replace?: boolean }) => void],
  { key = 'estado', label = 'Estado' }: { key?: string; label?: string } = {},
) {
  const keys = Object.keys(filters);
  const first = keys[0]!;
  const raw = params.get(key);
  const value = raw && filters[raw] ? raw : first;
  const filtered = rows?.filter(filters[value]!.match);
  const onChange = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === first) next.delete(key);
    else next.set(key, v);
    next.delete('pagina');
    setParams(next, { replace: true });
  };
  return {
    value,
    rows: filtered,
    quick: {
      label,
      value,
      onChange,
      options: keys.map((k) => ({ value: k, label: filters[k]!.label, count: rows ? rows.filter(filters[k]!.match).length : undefined })),
    },
  };
}
